import { useId } from 'react';
import {
    type Control,
    Controller,
    type FieldPath,
    type FieldValues,
    type RegisterOptions,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import TextField from '@/components/atoms/TextField';

import FormField from './FormField';

interface FormNumberFieldProps<TFieldValues extends FieldValues> {
    control: Control<TFieldValues>;
    name: FieldPath<TFieldValues>;
    label: string;
    placeholder?: string;
    min?: number;
    max?: number;
    step?: number | string;
    rules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>;
    errorMessage?: string;
    optional?: boolean;
}

// Stores its value as a string in form state (matching the existing modals);
// callers do the `Number(...)` cast when building the request body. Avoids
// the RHF `valueAsNumber` mode that turns empty inputs into NaN.
export default function FormNumberField<TFieldValues extends FieldValues>(
    props: FormNumberFieldProps<TFieldValues>
) {
    const { control, name, label, placeholder, min, max, step, rules, errorMessage, optional } =
        props;
    const id = useId();
    const { t } = useTranslation('fishGroups');

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field, fieldState }) => (
                <FormField
                    htmlFor={id}
                    label={label}
                    optional={optional}
                    error={
                        fieldState.error
                            ? fieldState.error.message ||
                              errorMessage ||
                              t('actions.errors.required')
                            : null
                    }>
                    <TextField
                        id={id}
                        type="number"
                        placeholder={placeholder}
                        min={min}
                        max={max}
                        step={step}
                        invalid={Boolean(fieldState.error)}
                        name={field.name}
                        value={(field.value as string | undefined) ?? ''}
                        onBlur={field.onBlur}
                        onChange={(e) => field.onChange(e.target.value)}
                    />
                </FormField>
            )}
        />
    );
}
